import React, { useState, useEffect, useRef, useCallback } from 'react';
// GTM Tracking Helper Functions
const trackCompanionEvent = (eventName, params) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: eventName,
        ...params,
    });
    // GTM event tracked
};
const CompanionWidget = ({ companion, state }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentMessage, setCurrentMessage] = useState('');
    const [currentLinkLabel, setCurrentLinkLabel] = useState('');
    const [currentLinkUrl, setCurrentLinkUrl] = useState('');
    const [showMessage, setShowMessage] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [currentMessageType, setCurrentMessageType] = useState('interactive');
    const [localState, setLocalState] = useState(state);
    // Animation state
    const [showPrimaryImage, setShowPrimaryImage] = useState(true);
    const [isAnimating, setIsAnimating] = useState(false);
    const animationStateRef = useRef({
        isRunning: false,
        currentStep: 0,
        totalSteps: 0,
        intervalId: null,
    });
    // Klaviyo pending data ref - stores companion data until form is submitted
    const pendingKlaviyoDataRef = useRef(null);
    // Chat input original z-index storage
    const chatInputOriginalZIndex = useRef(null);
    useEffect(() => {
        // Show widget after small delay for smooth entrance
        const timer = setTimeout(() => {
            setIsVisible(true);
            // Start animation after a delay to let widget settle
            setTimeout(() => {
                requestAnimationFrame(() => {
                    startAnimation();
                });
            }, 2500);
        }, 500);
        return () => clearTimeout(timer);
    }, []);
    // Only determine initial message after widget is visible
    useEffect(() => {
        if (isVisible) {
            determineInitialMessage();
        }
    }, [isVisible]);
    // Listen for Klaviyo form submissions to attach properties to correct email
    useEffect(() => {
        const handleKlaviyoSubmit = (e) => {
            if (e.detail.type === 'submit' && e.detail.formId === companion.klaviyoFormId && pendingKlaviyoDataRef.current) {
                const klaviyoWindow = window;
                const email = e.detail.metaData?.$email;
                // Klaviyo form submitted
                if (email && klaviyoWindow.klaviyo?.push) {
                    klaviyoWindow.klaviyo.push([
                        'identify',
                        {
                            email,
                            [`interest_${pendingKlaviyoDataRef.current.companionId}`]: true,
                        },
                    ]);
                    // Klaviyo identify pushed
                }
                pendingKlaviyoDataRef.current = null;
            }
        };
        window.addEventListener('klaviyoForms', handleKlaviyoSubmit);
        return () => {
            window.removeEventListener('klaviyoForms', handleKlaviyoSubmit);
        };
    }, [companion.klaviyoFormId]);
    // Manage chat input z-index when message bubble shows/hides
    useEffect(() => {
        if (showMessage) {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                lowerChatInputZIndex();
            }, 50);
        }
        else {
            // Restore immediately when hiding
            restoreChatInputZIndex();
        }
    }, [showMessage]);
    // Cleanup on unmount - always restore
    useEffect(() => {
        return () => {
            // Force restoration on component unmount
            const chatButton = document.getElementById('chat-button');
            if (chatButton && chatInputOriginalZIndex.current !== null) {
                if (chatInputOriginalZIndex.current === 'auto') {
                    chatButton.style.removeProperty('z-index');
                }
                else {
                    chatButton.style.zIndex = chatInputOriginalZIndex.current;
                }
                // Cleanup: Restored chat-button z-index on unmount
            }
        };
    }, []);
    const animationStep = useCallback(() => {
        const animationState = animationStateRef.current;
        if (!animationState.isRunning)
            return;
        animationState.currentStep++;
        if (animationState.currentStep >= animationState.totalSteps) {
            // Animation complete - reset to primary image and stop
            animationState.isRunning = false;
            if (animationState.intervalId) {
                clearInterval(animationState.intervalId);
                animationState.intervalId = null;
            }
            setIsAnimating(false);
            setShowPrimaryImage(true);
        }
        else {
            // Toggle between primary and secondary
            const shouldShowPrimary = animationState.currentStep % 2 === 1;
            setShowPrimaryImage(shouldShowPrimary);
        }
    }, []);
    // Animation effect using refs for stable timing
    useEffect(() => {
        if (isAnimating && companion.secondaryImage && companion.animationDuration && companion.animationCount) {
            const animationState = animationStateRef.current;
            // Reset animation state
            animationState.currentStep = 0;
            animationState.totalSteps = companion.animationCount * 2;
            animationState.isRunning = true;
            // Start with secondary image immediately
            setShowPrimaryImage(false);
            // Create interval for consistent timing
            animationState.intervalId = setInterval(animationStep, companion.animationDuration);
        }
        return () => {
            const animationState = animationStateRef.current;
            if (animationState.intervalId) {
                clearInterval(animationState.intervalId);
                animationState.intervalId = null;
                animationState.isRunning = false;
            }
        };
    }, [isAnimating, companion.secondaryImage, companion.animationDuration, companion.animationCount, animationStep]);
    const checkPurchaseStatus = () => {
        const companionSystem = window.companionSystem;
        const cartItems = companionSystem?.cartItems || [];
        // Checking purchase status for character
        // Check if any of the companion's variant IDs are in the cart
        const hasVariantInCart = companion.variantIds.some((variantId) => cartItems.includes(variantId));
        // Has character variant in cart check complete
        return hasVariantInCart;
    };
    const checkCompanionVariantsStock = () => {
        // Checking companion individual variants stock status
        const companionSystem = window.companionSystem;
        const companionProducts = companionSystem?.companionProducts || [];
        // Find variants that match the companion's variant IDs
        const companionVariants = [];
        companionProducts.forEach((product) => {
            product.variants.forEach((variant) => {
                if (companion.variantIds.includes(variant.id)) {
                    companionVariants.push(variant);
                }
            });
        });
        // Found companion variants
        if (companionVariants.length === 0) {
            // No companion variants found in products
            return false; // No variants found, consider out of stock
        }
        // Check if ALL companion variants are out of stock
        const allVariantsOutOfStock = companionVariants.every((variant) => !variant.available);
        // All companion variants stock check complete
        return !allVariantsOutOfStock; // Return true if variants are available, false if all out of stock
    };
    const startAnimation = () => {
        // Only start if we have all required animation data
        if (companion.secondaryImage && companion.animationDuration && companion.animationCount) {
            setIsAnimating(true);
        }
        else {
            setShowPrimaryImage(true);
        }
    };
    const lowerChatInputZIndex = () => {
        const chatButton = document.getElementById('chat-button');
        if (chatButton) {
            // Store original z-index if not already stored
            if (chatInputOriginalZIndex.current === null) {
                chatInputOriginalZIndex.current = chatButton.style.zIndex || getComputedStyle(chatButton).zIndex || 'auto';
            }
            // Set to lower z-index
            chatButton.style.zIndex = '899';
            // Lowered chat-button z-index
        }
    };
    const restoreChatInputZIndex = () => {
        const chatButton = document.getElementById('chat-button');
        if (chatButton && chatInputOriginalZIndex.current !== null) {
            // Restore original z-index
            if (chatInputOriginalZIndex.current === 'auto') {
                chatButton.style.removeProperty('z-index');
            }
            else {
                chatButton.style.zIndex = chatInputOriginalZIndex.current;
            }
            // Restored chat-button z-index
            chatInputOriginalZIndex.current = null;
        }
    };
    const determineInitialMessage = () => {
        // Get current page type from companion system config
        const companionSystem = window.companionSystem;
        const currentPage = companionSystem?.currentPage;
        if (!currentPage)
            return;
        // Check if companion's individual variants are out of stock
        const companionVariantsAvailable = checkCompanionVariantsStock();
        if (!companionVariantsAvailable) {
            // Companion variants out of stock - checking OOS message
            // Check if current URL matches any of the trigger URLs
            const currentPath = window.location.pathname;
            const isOnTriggerUrl = companion.oosTriggerUrls.some((triggerUrl) => {
                // Extract pathname from full URLs or use as-is if already a path
                const triggerPath = triggerUrl.startsWith('http') ? new URL(triggerUrl).pathname : triggerUrl;
                // Check exact match or if current path starts with trigger path
                return currentPath === triggerPath || currentPath.startsWith(triggerPath);
            });
            // OOS trigger URL check
            // Show OOS message if not shown yet, message exists, and on trigger URL
            if (!localState.oos_shown && companion.messages.oos.text && isOnTriggerUrl) {
                // Showing out of stock message on trigger URL
                setCurrentMessage(companion.messages.oos.text);
                setCurrentLinkLabel(companion.messages.oos.linkLabel);
                setCurrentLinkUrl(companion.messages.oos.linkUrl);
                setCurrentMessageType('oos');
                setIsClosing(false);
                setShowMessage(true);
                updateStateInStorage('oos_shown', true);
                // Track message shown
                trackCompanionEvent('companion_message_shown', {
                    character_id: companion.id,
                    message_type: 'oos',
                });
                return;
            }
            // Otherwise set up first interactive message but don't show it
            if (companion.messages.interactive.length > 0) {
                const firstMessage = companion.messages.interactive[0];
                setCurrentMessage(firstMessage.text);
                setCurrentLinkLabel(firstMessage.linkLabel);
                setCurrentLinkUrl(firstMessage.linkUrl);
                setCurrentMessageType('interactive');
                setIsClosing(false);
            }
            return;
        }
        // Check if customer already has character variants in cart
        const hasCharacterInCart = checkPurchaseStatus();
        // If customer has character variants in cart, show friends message instead
        if (hasCharacterInCart) {
            // Character variants already in cart - checking friends message
            // Show friends message on homepage or PDP if not shown yet and message exists
            if (!localState.friends_push && companion.messages.friends.text) {
                const companionSystem = window.companionSystem;
                const isCompanionProduct = companionSystem?.companionProductIds?.includes(currentPage.productId);
                const shouldShowFriendsMessage = currentPage.type === 'index' || (currentPage.type === 'product' && !isCompanionProduct);
                if (shouldShowFriendsMessage) {
                    // Showing friends message
                    setCurrentMessage(companion.messages.friends.text);
                    setCurrentLinkLabel(companion.messages.friends.linkLabel);
                    setCurrentLinkUrl(companion.messages.friends.linkUrl);
                    setCurrentMessageType('friends');
                    setIsClosing(false);
                    setShowMessage(true);
                    updateStateInStorage('friends_push', true);
                    // Track message shown
                    trackCompanionEvent('companion_message_shown', {
                        character_id: companion.id,
                        message_type: 'friends',
                    });
                    return;
                }
            }
            // Otherwise set up first interactive message but don't show it
            if (companion.messages.interactive.length > 0) {
                const firstMessage = companion.messages.interactive[0];
                setCurrentMessage(firstMessage.text);
                setCurrentLinkLabel(firstMessage.linkLabel);
                setCurrentLinkUrl(firstMessage.linkUrl);
                setCurrentMessageType('interactive');
                setIsClosing(false);
            }
            return;
        }
        // Homepage message logic - show automatically on homepage
        if (currentPage.type === 'index' && !localState.homepage_shown) {
            setCurrentMessage(companion.messages.homepage.text);
            setCurrentLinkLabel(companion.messages.homepage.linkLabel);
            setCurrentLinkUrl(companion.messages.homepage.linkUrl);
            setCurrentMessageType('homepage');
            setIsClosing(false);
            setShowMessage(true);
            updateStateInStorage('homepage_shown', true);
            // Track message shown
            trackCompanionEvent('companion_message_shown', {
                character_id: companion.id,
                message_type: 'homepage',
            });
            return;
        }
        // PDP message logic - show automatically on product pages (unless it's a companion product)
        if (currentPage.type === 'product' && !localState.pdp_shown) {
            // Check if current product is in the companion products list (prevent showing on companion product pages)
            const companionSystem = window.companionSystem;
            const isCompanionProduct = companionSystem?.companionProductIds?.includes(currentPage.productId);
            if (!isCompanionProduct) {
                setCurrentMessage(companion.messages.pdp.text);
                setCurrentLinkLabel(companion.messages.pdp.linkLabel);
                setCurrentLinkUrl(companion.messages.pdp.linkUrl);
                setCurrentMessageType('pdp');
                setIsClosing(false);
                setShowMessage(true);
                updateStateInStorage('pdp_shown', true);
                // Track message shown
                trackCompanionEvent('companion_message_shown', {
                    character_id: companion.id,
                    message_type: 'pdp',
                });
                return;
            }
        }
        // For other pages, prepare first interactive message but don't show
        if (companion.messages.interactive.length > 0) {
            const firstMessage = companion.messages.interactive[0];
            setCurrentMessage(firstMessage.text);
            setCurrentLinkLabel(firstMessage.linkLabel);
            setCurrentLinkUrl(firstMessage.linkUrl);
            setCurrentMessageType('interactive');
            setIsClosing(false);
            // Don't setShowMessage(true) here - wait for click
        }
    };
    const cycleInteractiveMessage = () => {
        // Cycling interactive message
        // Get next available interactive message
        const availableMessages = companion.messages.interactive.filter((_, index) => !localState.messages_shown.includes(index));
        // Checking available messages
        if (availableMessages.length > 0) {
            // Show next available message
            const selectedMessage = availableMessages[0];
            const originalIndex = companion.messages.interactive.findIndex((msg) => msg.text === selectedMessage.text);
            // Showing message
            setCurrentMessage(selectedMessage.text);
            setCurrentLinkLabel(selectedMessage.linkLabel);
            setCurrentLinkUrl(selectedMessage.linkUrl);
            setCurrentMessageType('interactive');
            setIsClosing(false);
            setShowMessage(true);
            // Track message clicked (1-based index as requested)
            trackCompanionEvent('companion_message_clicked', {
                character_id: companion.id,
                message_index: originalIndex + 1,
            });
            // Mark this message as shown
            const newMessagesShown = [...localState.messages_shown, originalIndex];
            updateStateInStorage('messages_shown', newMessagesShown);
        }
        else {
            // All messages shown, reset and start over
            // All messages shown, resetting
            updateStateInStorage('messages_shown', []);
            const firstMessage = companion.messages.interactive[0];
            setCurrentMessage(firstMessage.text);
            setCurrentLinkLabel(firstMessage.linkLabel);
            setCurrentLinkUrl(firstMessage.linkUrl);
            setCurrentMessageType('interactive');
            setIsClosing(false);
            setShowMessage(true);
            // Track message clicked (first message, index 1)
            trackCompanionEvent('companion_message_clicked', {
                character_id: companion.id,
                message_index: 1,
            });
            updateStateInStorage('messages_shown', [0]);
        }
    };
    const updateStateInStorage = (key, value) => {
        try {
            const currentState = JSON.parse(localStorage.getItem('companion-state') || '{}');
            currentState[key] = value;
            localStorage.setItem('companion-state', JSON.stringify(currentState));
            // Update local state to reflect changes immediately
            setLocalState((prevState) => ({
                ...prevState,
                [key]: value,
            }));
        }
        catch (error) {
            console.error('Failed to update companion state:', error);
        }
    };
    const handleMessageClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Message CTA clicked
        // Track CTA click
        trackCompanionEvent('companion_cta_clicked', {
            character_id: companion.id,
            message_type: currentMessageType,
        });
        // Check if this is an OOS message with Klaviyo form enabled
        if (currentMessageType === 'oos' && companion.enableKlaviyoForm && companion.klaviyoFormId) {
            // Opening Klaviyo form
            // Try to open Klaviyo form
            const klaviyoWindow = window;
            if (klaviyoWindow.klaviyo?.openForm) {
                // Store pending data - will be pushed after form submit with correct email
                pendingKlaviyoDataRef.current = { companionId: companion.id };
                // Stored pending Klaviyo data
                klaviyoWindow.klaviyo.openForm(companion.klaviyoFormId);
                // Close the message when form is opened
                setIsClosing(true);
                setTimeout(() => {
                    setShowMessage(false);
                    setIsClosing(false);
                }, 300);
            }
            else {
                console.warn('Klaviyo not loaded or openForm not available');
                // Fallback to regular link behavior if Klaviyo isn't loaded
                if (currentLinkUrl) {
                    window.location.href = currentLinkUrl;
                }
            }
        }
        else if (currentLinkUrl) {
            // Regular link behavior for non-Klaviyo messages
            window.location.href = currentLinkUrl;
        }
        else {
            // No URL found for message click
        }
    };
    const handleWidgetClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Widget clicked
        // Restart animation on click only if not already animating
        if (companion.secondaryImage && companion.animationDuration && companion.animationCount && !animationStateRef.current.isRunning) {
            setIsAnimating(true);
        }
        // Check message type to determine behavior
        if (showMessage && (currentMessageType === 'homepage' || currentMessageType === 'pdp' || currentMessageType === 'friends' || currentMessageType === 'oos')) {
            // For homepage/PDP/friends/OOS messages, do nothing (prompt customers to click the link)
            // Push message showing - click the link to proceed
            return;
        }
        if (showMessage && currentMessageType === 'interactive') {
            // Interactive message is showing, close it and cycle to next
            setIsClosing(true);
            setTimeout(() => {
                setShowMessage(false);
                setIsClosing(false);
                // Small buffer, then show next message
                setTimeout(() => {
                    cycleInteractiveMessage();
                }, 200);
            }, 300);
        }
        else if (!showMessage) {
            // Message is hidden, cycle to next interactive message and show
            cycleInteractiveMessage();
        }
    };
    const handleCloseMessage = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsClosing(true);
        // Wait for animation to complete before hiding
        setTimeout(() => {
            setShowMessage(false);
            setIsClosing(false);
        }, 300); // Match the animation duration
    };
    return (React.createElement("div", { className: `companion-widget ${isVisible ? 'companion-widget--visible' : ''}` },
        React.createElement("div", { className: "companion-widget__character", onClick: handleWidgetClick, style: { backgroundColor: companion.widgetBackgroundColor } },
            React.createElement("div", { className: "companion-widget__character-images" },
                React.createElement("img", { src: companion.image, alt: companion.displayName, className: `companion-widget__character-image companion-widget__character-image--primary ${showPrimaryImage ? 'companion-widget__character-image--visible' : 'companion-widget__character-image--hidden'} character-${companion.id}`, loading: "eager" }),
                companion.secondaryImage && (React.createElement("img", { src: companion.secondaryImage, alt: `${companion.displayName} animated`, className: `companion-widget__character-image companion-widget__character-image--secondary ${!showPrimaryImage ? 'companion-widget__character-image--visible' : 'companion-widget__character-image--hidden'} character-${companion.id}`, loading: "eager" })))),
        showMessage && currentMessage && (React.createElement("div", { className: `companion-widget__message ${isClosing ? 'companion-widget__message--closing' : ''}`, onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
            } },
            React.createElement("div", { className: "companion-widget__message-bubble", style: { backgroundColor: companion.messageBackgroundColor }, onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                } },
                React.createElement("button", { className: "companion-widget__message-close", onClick: handleCloseMessage, "aria-label": "Close message" },
                    React.createElement("svg", { width: "18", height: "18", viewBox: "0 0 18 18", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                        React.createElement("path", { d: "M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }))),
                React.createElement("p", { className: "companion-widget__message-text" }, currentMessage),
                currentLinkLabel && (React.createElement("button", { className: "companion-widget__message-cta", onClick: handleMessageClick }, currentLinkLabel)))))));
};
export default CompanionWidget;
